import { css, cx } from "@emotion/css"

import { StyledTable } from "components/Rable/StyledTable"

export type RadioTableColDef<T> = {
  header: string
  accessor?: keyof T
  render?: (row: T) => string | number
}

type Props<T extends {}> = {
  data: T[]
  columns: Array<RadioTableColDef<T>>
  getKey: (row: T) => string
  getDisabled: (row: T) => boolean
  onSelect: (key: string) => void
  header?: string
  selected?: string
  height?: number
}

export function RadioTable<T extends {}>(props: Props<T>) {
  const {
    data,
    columns,
    getKey,
    getDisabled,
    onSelect,
    header,
    selected,
    height,
  } = props
  const columnHeaders = columns.map((c) => c.header)

  const style = !!height
    ? ({
        "--height": `${height}px`,
      } as React.CSSProperties)
    : undefined

  return (
    <div className={styles.container} style={style}>
      {!!header ? <h4 className="BodyBody-1BlackBold">{header}</h4> : null}
      <StyledTable className={styles.table}>
        <thead>
          <th className={styles.checker}></th>
          {columnHeaders.map((h) => (
            <th className={cx("BodyBody2Regular", styles.tableHeader)}>{h}</th>
          ))}
        </thead>
        <tbody>
          {data.map((d) => {
            const key = getKey(d)
            const isDisabled = getDisabled(d)

            return (
              <tr key={key} onClick={(_) => !isDisabled && onSelect(key)}>
                <td>
                  {!isDisabled ? (
                    <div className={styles.radioRoot}>
                      <input
                        className={cx(styles.hidden, styles.radio)}
                        type="radio"
                        checked={selected === key}
                        id={key}
                        value={key}
                        onChange={(evt) => evt.preventDefault()}
                      />
                      <div
                        className={cx(
                          "BodyBody-2BlackLight",
                          styles.box,
                          "box",
                        )}
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </td>
                {columns.map((c) => {
                  if (!!c.accessor) return <td>{d[c.accessor]}</td>
                  if (!!c.render) return <td>{c.render(d)}</td>

                  return null
                })}
              </tr>
            )
          })}
        </tbody>
      </StyledTable>
    </div>
  )
}

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    max-height: var(--height, auto);
    overflow-y: auto;
    > h4 {
      padding-bottom: var(--spacing-m);
      position: sticky;
      top: 0;
      background-color: var(--white);
    }
  `,
  table: css`
    border: none;
    border-collapse: collapse;
    border-bottom: 1px solid var(--light-grey);
    tr {
      cursor: pointer;
    }
    th {
      text-align: start;
      font-weight: normal;
      border: none;
    }
    td {
      text-align: start;
      border: none;
      box-shadow: inset 0px 1px 0px var(--light-grey);
    }
  `,
  tableHeader: css`
    color: var(--dark-grey);
  `,
  checker: css`
    width: 24px;
  `,
  radioRoot: css`
    width: 18px;
    height: 18px;
    padding: 2px;
    border: 1px solid var(--primary-main);
    border-radius: 12px;
  `,
  hidden: css`
    position: absolute !important;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
    clip: rect(1px, 1px, 1px, 1px);
    white-space: nowrap;
  `,
  radio: css`
    width: var(--spacing-s);
    height: var(--spacing-s);
    :focus + .box {
      color: var(--primary-main);
    }

    :checked + .box {
      width: 12px;
      height: 12px;
      background-color: var(--primary-main);
      color: var(--primary-main);
      border-color: var(--primary-main);
    }

    :disabled + .box {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `,
  box: css`
    width: 100%;
    height: 100%;
    padding: var(--padding);
    display: flex;
    justify-content: flex-start;
    align-items: center;
    text-align: left;

    color: var(--color);
    background-color: var(--white);
    border-radius: 12px;

    text-decoration: none;

    :hover {
      cursor: pointer;
      background-color: #f5f5f5;
    }
  `,
}
